/**
 * Worker API client for the patrol surface.
 *
 * CREDENTIAL HANDLING — the partner is sent one link, `?k=pte_…`. On first load
 * we claim that token into localStorage and strip it out of the URL with
 * `history.replaceState`, then send it as `Authorization: Bearer` on every
 * request after. Two reasons: a token in a query string lands in edge logs and
 * the `Referer` header on every single call, and an officer who bookmarks or
 * shares the address bar would otherwise be sharing the credential. The link is
 * still the delivery mechanism, so it is exposed once — same trade the front-desk
 * tablet link already makes (docs/VALIDATION-TABLET-LINK-SPEC.md).
 *
 * TWO CREDENTIALS, in a fixed order. A Parkify staff member, or an operator's
 * own team member who has been given plate checks, signs in with Google instead
 * of opening a link, and holds an ops session JWT (the same one the console
 * uses) under a SEPARATE storage key. The partner credential always wins when
 * both are present: an officer's phone must keep behaving like an officer's
 * phone even if someone signed in on it once. See
 * api/src/enforcement/patrol_staff.ts and patrol_employee.ts for what the
 * server does with each.
 */
import { ApiError, createApiClient, resolveApiBase } from '@parkify/api-client'
import type { PatrolEnrollResponse } from '@parkify/api-types/patrol'

// Defaults to api.parkify.com (PARKIFY_API_ORIGIN in @parkify/api-client).
// Override with VITE_API_BASE, e.g. a local `wrangler dev`.
//
// `wrapNetworkErrors`: no response at all is "offline", and the browser's own
// wording ("Failed to fetch", "Load failed") is not copy. `cache: 'no-store'`:
// never let a proxy or the browser hand back a stale roster, because a cached
// "expired" is a wrongful citation. No client-level 401 hook: which credential
// to forget depends on which one the call sent (see `jget`).
const api = createApiClient({
  baseUrl: resolveApiBase({ VITE_API_BASE: import.meta.env.VITE_API_BASE as string | undefined }),
  cache: 'no-store',
  wrapNetworkErrors: true,
  errorMessage: ({ status }) => `Could not reach Parkify (HTTP ${status})`,
})

/** The server's `error` string on a failure, or '' when it sent none. */
const serverSaid = (e: ApiError): string => {
  const said = e.body?.error
  return typeof said === 'string' ? said : ''
}

const STORAGE_KEY = 'parkify.patrol.key'

/** Thrown when the credential is missing/revoked, so the app can show the
 *  "ask for a new link" screen rather than a generic error. */
export class PatrolAuthError extends Error {}

/**
 * A signed-in operator team member whose account does not include plate
 * checks, or whose operator is switched off. The server answers 403 with
 * `code: 'patrol_not_granted'`.
 *
 * A PatrolAuthError, so every screen that already leaves the roster on an auth
 * failure leaves it on this too, but the session is NOT forgotten: it is a good
 * session for the console, and signing in again would land on the same answer.
 * The app shows its own refusal for it, naming no zone.
 */
export class PatrolNotGrantedError extends PatrolAuthError {}

/** The server's code for PatrolNotGrantedError (api enforcement/patrol_router.ts). */
const NOT_GRANTED_CODE = 'patrol_not_granted'

/** A stored or presented credential this app will send: a shared link or a phone key. */
const isCredential = (k: string | null): k is string => !!k && (k.startsWith('pte_') || k.startsWith('ptv_'))

/** Fallback when storage throws (private-mode Safari): this tab only. */
let memoryKey: string | null = null

function scrubK(): void {
  const url = new URL(window.location.href)
  if (!url.searchParams.has('k')) return
  url.searchParams.delete('k')
  window.history.replaceState({}, '', url.pathname + url.search + url.hash)
}

/**
 * Claim `?k=` into storage and scrub it from the address bar. Idempotent, and
 * safe to call on every mount: with no `?k=` present it just reads what's
 * already stored. A one-time setup link (`pti_`) is NOT a credential and is
 * left for `enrollFromSetupLink` — it must be traded, never stored.
 */
export function claimCredential(): string | null {
  try {
    const fromUrl = new URL(window.location.href).searchParams.get('k')
    if (fromUrl && fromUrl.startsWith('pte_')) {
      localStorage.setItem(STORAGE_KEY, fromUrl)
      scrubK()
      return fromUrl
    }
    const stored = localStorage.getItem(STORAGE_KEY)
    return isCredential(stored) ? stored : memoryKey
  } catch {
    // Private-mode Safari throws on localStorage. Fall back to the URL value for
    // this session only — better a working session than a dead screen.
    const k = new URLSearchParams(window.location.search).get('k')
    return k && k.startsWith('pte_') ? k : memoryKey
  }
}

/** The one-time setup link in the address bar, if this open is a first-time setup. */
export function pendingSetupLink(): string | null {
  try {
    const k = new URL(window.location.href).searchParams.get('k')
    return k && k.startsWith('pti_') ? k : null
  } catch { return null }
}

/**
 * Trade a one-time setup link for this phone's own key (api: POST
 * /api/patrol/enroll). The link is scrubbed from the address bar once the
 * server has answered for it — on success, or on a definite "already used /
 * expired". A network failure or 503 leaves it in place so a reload retries:
 * the link has not been spent, and losing it would strand the officer.
 */
export async function enrollFromSetupLink(invite: string): Promise<string> {
  let res: Response
  try {
    res = await api.raw('/api/patrol/enroll', { method: 'POST', json: { invite } })
  } catch (e) {
    if (!(e instanceof ApiError)) throw e
    if (e.offline) throw new PatrolRequestError('No connection', { offline: true })
    if (e.status === 401) {
      scrubK()
      throw new PatrolAuthError(serverSaid(e) || 'Setup link rejected')
    }
    throw new PatrolRequestError(`HTTP ${e.status}`, { status: e.status })
  }
  const body = await res.json().catch(() => null) as PatrolEnrollResponse | null
  const key = body?.key
  if (!isCredential(key ?? null)) throw new PatrolRequestError('Bad enroll response', { status: res.status })
  try { localStorage.setItem(STORAGE_KEY, key!) } catch { memoryKey = key! }
  scrubK()
  return key!
}

export function forgetCredential(): void {
  memoryKey = null
  try { localStorage.removeItem(STORAGE_KEY) } catch { /* nothing to do */ }
}

// ─── Parkify staff session (Google sign-in) ──────────────────────────────────

const STAFF_KEY = 'parkify.patrol.staff'

export interface StaffSession {
  token: string
  email: string
  name: string
  /** ms epoch. Mirrors the worker's non-remembered ops TTL (8h). */
  expiresAt: number
}

/**
 * The stored staff session, or null.
 *
 * Expiry is checked here so an obviously-dead token never gets sent: the worker
 * would 401 it anyway, but that 401 would land mid-shift as "you were signed
 * out" instead of a clean sign-in screen on open.
 */
export function readStaffSession(): StaffSession | null {
  try {
    const raw = localStorage.getItem(STAFF_KEY)
    if (!raw) return null
    const s = JSON.parse(raw) as StaffSession
    if (!s.token || typeof s.expiresAt !== 'number' || Date.now() >= s.expiresAt) {
      localStorage.removeItem(STAFF_KEY)
      // Remember that there WAS a session and it ran out, so the screen can say
      // so. Removing the entry above would otherwise make a lapsed staff session
      // indistinguishable from a device that never had one.
      if (s.token) staffSessionLapsed = true
      return null
    }
    return s
  } catch { return null }
}

/**
 * True when the stored staff session was found past its 8h expiry (and removed)
 * since the last sign-in or sign-out. Lets a reopened app say "your session has
 * ended" instead of showing the first-time partner-link screen to someone who
 * did sign in earlier. Module state, not storage: the expired entry is deleted
 * on read, and it only needs to outlive that read for the life of the tab.
 */
let staffSessionLapsed = false
export function hasLapsedStaffSession(): boolean { return staffSessionLapsed }

export function forgetStaffSession(): void {
  staffSessionLapsed = false
  try { localStorage.removeItem(STAFF_KEY) } catch { /* nothing to do */ }
}

/** Non-remembered ops TTL — patrol never offers "remember me" (see signInWithGoogle). */
const STAFF_TTL_MS = 8 * 3600 * 1000

interface WorkerAuthResponse {
  token?: string
  email?: string
  name?: string
  clientId?: string | null
  error?: string
}

/**
 * Exchange a Google access token for an ops session JWT and store it.
 *
 * Deliberately never asks for "remember me": this is a screen someone opens to
 * look at a lot, often on a shared or borrowed device, and an 8h session is
 * plenty. A 7-day patrol token on a phone in a truck is not a trade worth making
 * for a convenience nobody asked for.
 *
 * An operator's own team member signs in here too. Whether they may use patrol,
 * and in which zones, is the server's answer on the next call (their plate
 * check grant and zone access, read live), not something this client can tell
 * from the sign-in response. Without the grant that next call is a 403 the app
 * words as "Your account doesn't include patrol", with Sign out.
 */
export async function signInWithGoogle(accessToken: string): Promise<StaffSession> {
  let res: Response
  try {
    res = await api.raw('/api/auth/google', { method: 'POST', json: { accessToken, rememberMe: false } })
  } catch (e) {
    if (!(e instanceof ApiError)) throw e
    // Same mapping as `jget`: no response at all is "offline", and the
    // browser's own wording ("Failed to fetch", "Load failed") is not copy.
    if (e.offline) throw new PatrolRequestError('No connection', { offline: true })
    const said = e.body?.error
    throw new Error(typeof said === 'string' ? said : `Sign-in failed (${e.status})`)
  }
  const data = await res.json().catch(() => null) as WorkerAuthResponse | null
  if (!data?.token) throw new Error(data?.error ?? `Sign-in failed (${res.status})`)
  const session: StaffSession = {
    token: data.token,
    email: data.email ?? '',
    name: data.name ?? data.email ?? 'Parkify',
    expiresAt: Date.now() + STAFF_TTL_MS,
  }
  staffSessionLapsed = false
  try { localStorage.setItem(STAFF_KEY, JSON.stringify(session)) } catch { /* session-only */ }
  return session
}

/**
 * The credential to send, partner first. Returning WHICH one it is matters: a
 * 401 must clear only the credential that failed, or signing in on a phone that
 * also holds a partner link would wipe the officer's link.
 */
function credential(): { token: string; kind: 'partner' | 'staff' } | null {
  const key = claimCredential()
  if (key) return { token: key, kind: 'partner' }
  const staff = readStaffSession()
  return staff ? { token: staff.token, kind: 'staff' } : null
}

/**
 * A read that failed for a reason that is NOT "your credential is bad".
 *
 * Always carries a non-empty `message`. It used to be `body.error ?? statusText`,
 * and a 502/504/524 with an HTML body over HTTP/2 has neither (the body does not
 * parse as JSON and `statusText` is ''), so the message was '' (falsy) and any
 * screen that gated its error note on the message rendering showed nothing at all.
 * Screens must not render `message` to officers (it can be a browser's "Failed to
 * fetch" or server prose); they branch on `offline` and use their own plain copy.
 */
export class PatrolRequestError extends Error {
  /** True when no response arrived at all: no signal, DNS, a dropped connection. */
  readonly offline: boolean
  /** The HTTP status, or null when no response arrived. */
  readonly status: number | null
  constructor(message: string, opts: { offline?: boolean; status?: number | null } = {}) {
    super(message)
    this.name = 'PatrolRequestError'
    this.offline = opts.offline ?? false
    this.status = opts.status ?? null
  }
}

async function jget<T>(path: string, signal?: AbortSignal, json?: unknown): Promise<T> {
  const cred = credential()
  if (!cred) throw new PatrolAuthError('No patrol credential')
  try {
    return await api.request<T>(path, {
      bearer: cred.token,
      signal,
      // A read whose question is sensitive goes as a POST body (see checkPlate).
      ...(json === undefined ? {} : { method: 'POST', json }),
      // Only reached when the server has actually DECIDED the credential is bad:
      // bad format, no such record, or a deactivated client. Anything it merely
      // failed to look up answers 503 and leaves the token alone; see
      // api/src/enforcement/patrol_credentials.ts `PartnerOutcome`. Clears only
      // the credential that failed, so a staff sign-in on an officer's phone
      // never wipes the officer's link.
      onUnauthorized: () => { if (cred.kind === 'partner') forgetCredential(); else forgetStaffSession() },
    })
  } catch (e) {
    // An abort is the caller cancelling, not a failure; the client hands it
    // back untouched and callers already check their own signal.
    if (!(e instanceof ApiError)) throw e
    if (e.offline) throw new PatrolRequestError('No connection', { offline: true })
    if (e.status === 401) {
      // Fallback wording only: every current screen renders its own copy off
      // PatrolAuthError rather than this string. It says "rejected", not
      // "expired": a shared link has no lifetime, and a phone key only lapses
      // after 60 days unused, so "expired" would mislead in almost every case.
      throw new PatrolAuthError(serverSaid(e) || 'Credential rejected')
    }
    // A team member without plate checks. Keyed on the code, never the status
    // alone: a 403 is also the answer for a lot outside the caller's zones.
    if (e.status === 403 && e.code === NOT_GRANTED_CODE) {
      throw new PatrolNotGrantedError(typeof e.body?.error === 'string' ? e.body.error : 'Patrol is not included')
    }
    // A 200 whose body is not JSON (a captive portal, a proxy's HTML page) is a
    // failed read, not a roster. The client's message is already officer-safe.
    // Everything else (503 included: "we couldn't confirm your account just
    // now" is transient and deliberately NOT an auth failure) keeps the
    // server's words when it sent some, and the client's fallback otherwise.
    throw new PatrolRequestError(e.message, { status: e.status })
  }
}

// ─── shapes: @parkify/api-types/patrol (derived from the Worker's own types) ──
//
// Re-exported under the names this app has always used, so no screen changes.
// The package's header lists the producing server file for each one.
import type {
  PatrolBayResult, PatrolCallerKind, PatrolPlateCheck, PatrolZoneRef, PatrolZoneSnapshot, PatrolZonesResponse,
} from '@parkify/api-types/patrol'
export type {
  PatrolVerdict as Verdict,
  PatrolSource as Source,
  PatrolVerdictReason as VerdictReason,
  PatrolVehicle as Vehicle,
  PatrolFacilityZoneAnswer as FacilityZoneAnswer,
  PatrolFacilityZoneState as FacilityZoneState,
  PatrolZoneClosure as ZoneClosure,
  PatrolZoneSnapshot as Snapshot,
  PatrolCallerKind as CallerKind,
  PatrolBayResult as BayResult,
  PatrolCandidate as Candidate,
} from '@parkify/api-types/patrol'

/**
 * One picker entry: a zone, or a whole LOCATION (a facility holding two or more
 * of the caller's zones). A facility entry rides the same screens as a zone, so
 * it carries an id no zone can have (`facility:<id>`) and `facility` naming the
 * zones it spans. Every read below routes on that prefix.
 */
export type ZoneRef = PatrolZoneRef & { facility?: { id: string; zones: { id: string; name: string }[] } }

const FACILITY_PREFIX = 'facility:'

/** A facility's picker id, or null for a plain zone id. */
export function facilityIdOf(pickerId: string): string | null {
  return pickerId.startsWith(FACILITY_PREFIX) ? pickerId.slice(FACILITY_PREFIX.length) : null
}

/**
 * The picker list: facilities first, then every zone as before. A Worker that
 * predates facilities sends none, and the list is the zones alone. A facility
 * never offers the bay lookup: bays are numbered per zone.
 */
export function pickerEntries(r: PatrolZonesResponse): ZoneRef[] {
  const facilities: ZoneRef[] = (r.facilities ?? []).map(f => ({
    id: `${FACILITY_PREFIX}${f.id}`,
    name: f.name,
    address: f.address,
    reservedBays: false,
    facility: { id: f.id, zones: f.zones },
  }))
  return [...facilities, ...r.zones]
}

export function callerKindOf(r: { staff?: boolean; kind?: string }): PatrolCallerKind {
  if (r.kind === 'employee' || r.kind === 'staff' || r.kind === 'partner') return r.kind
  return r.staff ? 'staff' : 'partner'
}

export function fetchZones(signal?: AbortSignal): Promise<PatrolZonesResponse> {
  return jget('/api/patrol/zones', signal)
}

export function fetchRoster(zoneId: string, signal?: AbortSignal): Promise<PatrolZoneSnapshot> {
  const facilityId = facilityIdOf(zoneId)
  if (facilityId !== null) return jget(`/api/patrol/vehicles?facilityId=${encodeURIComponent(facilityId)}`, signal)
  return jget(`/api/patrol/vehicles?zoneId=${encodeURIComponent(zoneId)}`, signal)
}

/** "Is this bay reserved for someone else?" (RESERVED-BAYS-SPEC §3). `unknown`
 *  means the Worker could not read the inventory: render "we can't say". */
export function checkBay(zoneId: string, bay: string, signal?: AbortSignal): Promise<PatrolBayResult> {
  return jget(`/api/patrol/bay?zoneId=${encodeURIComponent(zoneId)}&bay=${encodeURIComponent(bay)}`, signal)
}

/** One plate, plus near-miss candidates when the answer wasn't `authorized`.
 *  The closure is resolved at lookup time, not taken from the last roster.
 *
 *  Sent as `POST { zoneId, plate }`, never a query string: a request URL is
 *  recorded in the Worker's logs and traces, and a plate is personal data
 *  (#4504). The Worker answers the POST exactly as it answers the older GET.
 *
 *  A 404 to the POST means a Worker from before the POST existed (it refuses
 *  every non-GET on this surface before auth); a current Worker never answers
 *  it 404. Pages and the Worker deploy independently, so that one answer gets
 *  the older GET rather than a dead lookup. Goes with the GET's removal. */
export async function checkPlate(zoneId: string, plate: string, signal?: AbortSignal): Promise<PatrolPlateCheck> {
  // A facility lookup has no GET twin to fall back to: a Worker old enough to
  // 404 the POST never offered a facility in the first place.
  const facilityId = facilityIdOf(zoneId)
  if (facilityId !== null) return jget<PatrolPlateCheck>('/api/patrol/plate', signal, { facilityId, plate })
  try {
    return await jget<PatrolPlateCheck>('/api/patrol/plate', signal, { zoneId, plate })
  } catch (e) {
    if (!(e instanceof PatrolRequestError) || e.status !== 404) throw e
    return jget(`/api/patrol/plate?zoneId=${encodeURIComponent(zoneId)}&plate=${encodeURIComponent(plate)}`, signal)
  }
}
